/** Pinyin syllables with tone marks. Lesson pinyin is saved with tone marks (wǒ), which pass through;
 * rows saved before that keep tone numbers (wo3), which are converted for display. */
export function instructionalPinyin(tokens: string[] | null | undefined): string {
    const marks: Record<string, string> = { a: 'āáǎà', e: 'ēéěè', i: 'īíǐì', o: 'ōóǒò', u: 'ūúǔù', ü: 'ǖǘǚǜ' };
    return (tokens ?? []).map(token => token.replace(/([a-zü:]+)([0-5])/gi, (_match, syllable: string, tone: string) => {
        const normalized = syllable.replace(/u:|v/gi, value => value === value.toUpperCase() ? 'Ü' : 'ü');
        if (tone === '0' || tone === '5') return normalized;
        const lower = normalized.toLowerCase();
        let index = lower.indexOf('a');
        if (index < 0) index = lower.indexOf('e');
        if (index < 0 && lower.includes('ou')) index = lower.indexOf('o');
        if (index < 0) for (let i = lower.length - 1; i >= 0; i--) if (marks[lower[i]]) { index = i; break; }
        if (index < 0) return normalized + tone;
        let vowel = marks[lower[index]][Number(tone) - 1];
        if (normalized[index] !== lower[index]) vowel = vowel.toUpperCase();
        return normalized.slice(0, index) + vowel + normalized.slice(index + 1);
    })).join(' ');
}

const toneByMark: Record<string, string> = { '̄': '1', '́': '2', '̌': '3', '̀': '4' };
const isVowel = (char: string | undefined) => Boolean(char && /[aeiouvü]/i.test(char.normalize('NFC')));

/** Space-separated numeric-tone text for an edit field, so wǒ is edited as wo3 and a neutral-tone
 * syllable saved without a mark (ma) as ma5. Each tone number goes after its syllable's vowels and
 * any final n, ng or r that does not start the next syllable. The server saves the numbers as marks. */
export function editablePinyin(tokens: string[] | null | undefined): string {
    return (tokens ?? []).map(token => {
        const chars = Array.from(token.normalize('NFD'));
        let out = '';
        for (let i = 0; i < chars.length; i++) {
            const tone = toneByMark[chars[i + 1]];
            if (!tone) { out += chars[i]; continue; }
            out += chars[i];
            i += 2;
            while (i < chars.length && (isVowel(chars[i]) || chars[i] === '̈') && !toneByMark[chars[i + 1]]) out += chars[i++];
            for (const finals of [['ng', 'n'], ['r']]) for (const final of finals) {
                const next = chars.slice(i, i + final.length).join('');
                if (next.toLowerCase() === final && !isVowel(chars[i + final.length])) { out += next; i += final.length; break; }
            }
            out += tone;
            i--;
        }
        out = out.normalize('NFC');
        return /\d/.test(out) || !pinyinSyllable.test(out.toLowerCase().replace(/u:|v/g, 'ü')) ? out : `${out}5`;
    }).join(' ');
}

const pinyinSyllable = /^(?:zh|ch|sh|[bpmfdtnlgkhjqxrzcsyw])?(?:a|ai|an|ang|ao|e|ei|en|eng|er|i|ia|ian|iang|iao|ie|in|ing|iong|iu|o|ong|ou|u|ua|uai|uan|uang|ue|ui|un|uo|ü|üe|üan|ün)r?$/;

/** Tone marks for numeric-tone pinyin inside free text such as plan sections and materials, for
 * text saved before the server converted it. A run is changed only when every syllable in it is
 * real pinyin, so "15 yuan", "mp3" or a level such as "A1" stays as written. The server's
 * tone_marked_text() applies the same rule when lesson text is saved. */
export function instructionalPinyinText(text: string | null | undefined): string {
    return (text ?? '').replace(/(?<![A-Za-zü:0-9])(?:[A-Za-zü:]+[1-5])+(?![A-Za-zü:0-9])/g, run => {
        if (run === run.toUpperCase()) return run;
        const syllables = Array.from(run.matchAll(/([A-Za-zü:]+)[1-5]/g), match => match[1].toLowerCase().replace(/u:|v/g, 'ü'));
        return syllables.every(syllable => pinyinSyllable.test(syllable)) ? instructionalPinyin([run]) : run;
    });
}
